import { useEffect, useRef, useState, useCallback, RefObject } from 'react';

export interface PullToRefreshState {
  isPulling: boolean;
  pullDistance: number;
  isRefreshing: boolean;
}

interface UsePullToRefreshOptions {
  /**
   * Callback function to execute when refresh is triggered
   */
  onRefresh: () => Promise<void>;
  /**
   * Maximum pull distance before triggering refresh (in pixels)
   * @default 80
   */
  pullThreshold?: number;
  /**
   * Maximum pull distance to prevent over-pulling (in pixels)
   * @default 150
   */
  maxPullDistance?: number;
  /**
   * Whether pull-to-refresh is enabled
   * @default true
   */
  enabled?: boolean;
}

/**
 * Custom hook for implementing pull-to-refresh gesture on mobile devices
 * 
 * @param containerRef - Ref to the scrollable container element
 * @param options - Configuration options
 * @returns Pull-to-refresh state
 */
export const usePullToRefresh = (
  containerRef: RefObject<HTMLElement>,
  options: UsePullToRefreshOptions
): PullToRefreshState => {
  const {
    onRefresh,
    pullThreshold = 80,
    maxPullDistance = 150,
    enabled = true,
  } = options;

  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Refs to track state values for avoiding stale closures in event handlers
  const isPullingRef = useRef(false);
  const pullDistanceRef = useRef(0);
  const touchStartY = useRef<number | null>(null);

  // Keep refs in sync with state
  useEffect(() => {
    isPullingRef.current = isPulling;
  }, [isPulling]);

  useEffect(() => {
    pullDistanceRef.current = pullDistance;
  }, [pullDistance]);

  /**
   * Check whether the user is truly scrolled to the top of the page.
   * The container element may not itself be the scroll container (e.g. when it
   * has overflow-y: auto but no fixed height constraint, so it grows with its
   * content and scrollTop stays 0). We walk up through parent elements and also
   * check window.scrollY to catch all scrollable ancestors.
   */
  const isScrolledToTop = useCallback((element: HTMLElement): boolean => {
    if (element.scrollTop > 0) return false;

    let parent = element.parentElement;
    while (parent) {
      if (parent.scrollTop > 0) return false;
      parent = parent.parentElement;
    }

    return window.scrollY <= 0;
  }, []);

  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    const container = containerRef.current;

    const handleTouchStart = (e: TouchEvent) => {
      // Only start pull gesture if truly at the top of all scroll containers
      if (!isScrolledToTop(container) || isRefreshing) return;

      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartY.current === null || isRefreshing) return;

      const currentY = e.touches[0].clientY;
      const pullDelta = currentY - touchStartY.current;

      // Only activate pull when truly scrolled to top and pulling down
      if (!isScrolledToTop(container) || pullDelta <= 0) {
        setIsPulling(false);
        setPullDistance(0);
        touchStartY.current = null;
        return;
      }

      // Apply resistance to pull distance (makes it feel more natural)
      const resistance = 0.5;
      const calculatedDistance = Math.min(
        pullDelta * resistance,
        maxPullDistance
      );

      setIsPulling(true);
      setPullDistance(calculatedDistance);

      // Prevent default scrolling when pulling
      if (calculatedDistance > 10) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = async () => {
      // Use refs to avoid stale closure issues
      if (!isPullingRef.current || isRefreshing) {
        touchStartY.current = null;
        setIsPulling(false);
        setPullDistance(0);
        return;
      }

      // Trigger refresh if pulled beyond threshold
      if (pullDistanceRef.current >= pullThreshold) {
        setIsRefreshing(true);
        setIsPulling(false);
        
        try {
          await onRefresh();
        } catch (error) {
          console.error('Pull to refresh error:', error);
        } finally {
          setIsRefreshing(false);
          setPullDistance(0);
        }
      } else {
        // Reset if not pulled far enough
        setIsPulling(false);
        setPullDistance(0);
      }

      touchStartY.current = null;
    };

    // Add event listeners
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [
    enabled,
    containerRef,
    isRefreshing,
    isScrolledToTop,
    onRefresh,
    pullThreshold,
    maxPullDistance,
  ]);

  return {
    isPulling,
    pullDistance,
    isRefreshing,
  };
};

export default usePullToRefresh;
